import { Box, Button, Group, LoadingOverlay, Modal, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useDeleteDeploymentAlertSetting } from '@/queries/deploymentAlertQueries'
import { showToast } from '@/theme/notifications'

type DeleteNotificationRuleModalProps = {
  opened: boolean
  ruleId?: string
  deviceNames: string[]
  onClose: () => void
  onDeleted: () => void
}

export const DeleteNotificationRuleModal = ({
  opened,
  ruleId,
  deviceNames,
  onClose,
  onDeleted
}: DeleteNotificationRuleModalProps) => {
  const intl = useIntl()

  const { mutateAsync: deleteAlertSetting, isPending } =
    useDeleteDeploymentAlertSetting()

  const handleDelete = async () => {
    if (!ruleId) {
      return
    }

    try {
      await deleteAlertSetting(ruleId)

      showToast(
        intl.formatMessage({ id: 'workspace.notifications.delete.success' }),
        'success'
      )
      onDeleted()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'workspace.notifications.delete.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="workspace.notifications.delete.title" />}
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Text size="sm" mb="xl">
          <FormattedMessage
            id="workspace.notifications.delete.confirmation"
            values={{ devices: <span>{deviceNames.join(', ')}</span> }}
          />
        </Text>

        <Group justify="end">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button miw={120} color="red" onClick={() => void handleDelete()}>
            <FormattedMessage id="delete" />
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}
